import type { ColumnMapping } from '../types';

/**
 * Check if any date values in the mapped date column are ambiguous.
 * A date is ambiguous if both parts could be month or day (both <= 12).
 * Returns a sample ambiguous date string if found, null otherwise.
 */
export function detectAmbiguousDates(
  rows: Record<string, string>[],
  mappings: ColumnMapping[],
): { ambiguousExample: string; possibleUS: string; possibleEU: string } | null {
  const dateMapping = mappings.find((m) => m.targetField === 'acquisition_date');
  if (!dateMapping) return null;

  for (const row of rows) {
    const val = row[dateMapping.sourceColumn]?.trim();
    if (!val) continue;

    // Match patterns like 3/4/2025, 03-04-2025, 3.4.25
    const match = val.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
    if (!match) continue;

    const a = parseInt(match[1]!, 10);
    const b = parseInt(match[2]!, 10);

    // Ambiguous only if both could be a valid month (1-12)
    if (a >= 1 && a <= 12 && b >= 1 && b <= 12 && a !== b) {
      // Build human-readable examples
      const months = [
        '',
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const yearStr = match[3]!;
      const year = yearStr.length === 2 ? `20${yearStr}` : yearStr;

      return {
        ambiguousExample: val,
        possibleUS: `${months[a]} ${b}, ${year}`, // MM/DD
        possibleEU: `${months[b]} ${a}, ${year}`, // DD/MM
      };
    }
  }

  return null;
}
